import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArrowLeft, CheckCircle2, CopyPlus, Lock, RotateCcw, Trash2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { ConfirmDialog } from 'components/ui/confirm-dialog';
import { Input } from 'components/ui/input';
import Tooltip from 'components/ui/tooltip';
import { useToast } from 'components/ui/use-toast';
import { cn } from 'lib/utils';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import * as Api from 'modules/standardizedExam/api';
import { KIND_LABEL_KEY, isSatKind } from 'modules/standardizedExam/constants';
import { AudioTracksEditor } from 'modules/standardizedExam/components/authoring/AudioTracksEditor';
import { ExamQuestionList } from 'modules/standardizedExam/components/authoring/ExamQuestionList';
import type { QuestionDifficulty, UpdateBlockInput } from 'modules/standardizedExam/types';

const TIER_KEY: Record<string, string> = { BASE: 'exam.sat.module1', EASY: 'exam.sat.module2_easy', HARD: 'exam.sat.module2_hard' };

/** Edit one library section or SAT module set. Ready blocks are frozen; changes go through a new version. */
export const SectionBlockPage: React.FC = () => {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: block, isLoading, isError } = useQuery({
    queryKey: ['section-library', 'block', id],
    queryFn: async () => (await Api.LibraryGet(id)).data,
    enabled: Boolean(id),
  });

  const [title, setTitle] = useState('');
  const [tags, setTags] = useState('');
  const [topic, setTopic] = useState('');
  const [threshold, setThreshold] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const canDelete = usePermission(PERMISSIONS.TESTS_DELETE);

  useEffect(() => {
    if (!block) return;
    setTitle(block.title);
    setTags(block.tags.join(', '));
    setTopic(block.topic ?? '');
    setThreshold(typeof block.routingThreshold === 'number' ? String(block.routingThreshold) : '');
  }, [block]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['section-library'] });

  const act = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    try {
      await fn();
      await refresh();
      if (ok) toast.success(ok);
    } catch (e: any) {
      const issues: string[] | undefined = e?.response?.data?.issues;
      toast.error(issues?.length ? issues.join(' · ') : e?.response?.data?.message || t('failed_to_save'));
    } finally {
      setBusy(false);
    }
  };

  const update = (data: UpdateBlockInput) => act(() => Api.LibraryUpdate(id, data));

  const remove = async () => {
    setBusy(true);
    try {
      await Api.LibraryDelete(id);
      toast.success(t('exam.library.deleted'));
      navigate('/app/section-library');
      queryClient.removeQueries({ queryKey: ['section-library', 'block', id] });
      refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('delete_failed'));
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) return <div className="mx-auto h-64 max-w-5xl animate-pulse rounded-xl bg-muted" />;
  if (isError || !block) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <p className="text-muted-foreground">{t('exam.library.not_found')}</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/app/section-library">{t('exam.library.back')}</Link>
        </Button>
      </div>
    );
  }

  const draft = block.status === 'DRAFT';
  const sat = isSatKind(block.kind);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to="/app/section-library" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" aria-hidden /> {t('exam.library.title')}
          </Link>
          <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {t(KIND_LABEL_KEY[block.kind])} · <span className="font-mono">{block.code}</span>
          </p>
          <h1 className="font-display text-2xl font-bold text-foreground">{block.title}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {draft && (
            <Button onClick={() => act(() => Api.LibraryReady(id), t('exam.library.now_ready'))} disabled={busy} className="gap-1.5">
              <CheckCircle2 className="h-4 w-4" aria-hidden /> {t('exam.library.mark_ready')}
            </Button>
          )}
          {block.status === 'READY' && (
            <Button
              variant="outline"
              disabled={busy}
              className="gap-1.5"
              onClick={() =>
                act(async () => {
                  const { data } = await Api.LibraryNewVersion(id);
                  navigate(`/app/section-library/${data.id}`);
                })
              }
            >
              <CopyPlus className="h-4 w-4" aria-hidden /> {t('exam.library.new_version')}
            </Button>
          )}
          {block.status === 'ARCHIVED' ? (
            <Button variant="outline" disabled={busy} onClick={() => act(() => Api.LibraryRestore(id))} className="gap-1.5">
              <RotateCcw className="h-4 w-4" aria-hidden /> {t('exam.library.restore')}
            </Button>
          ) : (
            <Button variant="outline" disabled={busy} onClick={() => act(() => Api.LibraryArchive(id))} className="gap-1.5">
              <Archive className="h-4 w-4" aria-hidden /> {t('exam.library.archive')}
            </Button>
          )}
          {canDelete && (block.usageCount > 0 ? (
            <Tooltip content={t('exam.library.delete_in_use')} side="bottom" className="w-64 whitespace-normal">
              <Button variant="outline" disabled className="gap-1.5 text-destructive">
                <Trash2 className="h-4 w-4" aria-hidden /> {t('delete')}
              </Button>
            </Tooltip>
          ) : (
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => setConfirmDelete(true)}
              className="gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" aria-hidden /> {t('delete')}
            </Button>
          ))}
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('exam.library.delete_title')}
        description={t('exam.library.delete_body', { title: block.title, code: block.code })}
        confirmLabel={t('delete')}
        cancelLabel={t('cancel')}
        destructive
        loading={busy}
        onConfirm={remove}
      />

      {!draft && (
        <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">
          <Lock className="h-4 w-4" aria-hidden /> {block.status === 'READY' ? t('exam.library.frozen') : t('exam.library.archived_note')}
        </p>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-6">
          {sat ? null : block.kind === 'IELTS_LISTENING' ? (
            <section className="rounded-xl border border-border bg-card p-4 sm:p-5">
              <AudioTracksEditor
                value={block.stimulus}
                disabled={!draft}
                questionCount={block.parts.reduce((total, part) => total + part.questionCount, 0)}
                onChange={(stimulus) => update({ stimulus })}
              />
            </section>
          ) : null}

          {block.parts.map((part) => (
            <section key={part.contentId} className="space-y-3 rounded-xl border border-border bg-card p-4 sm:p-5">
              <h2 className="font-display text-base font-bold text-foreground">
                {part.tier ? t(TIER_KEY[part.tier]) : t('exam.library.questions')}
                <span className="ml-2 text-sm font-normal text-muted-foreground">{t('exam.library.n_questions', { count: part.questionCount })}</span>
              </h2>
              <ExamQuestionList
                kind={block.kind}
                questions={part.questions}
                uploadContext={`section-library/${block.id}`}
                disabled={!draft}
                onAdd={(payload) => act(() => Api.LibraryAddQuestion(id, part.contentId, payload))}
                onUpdate={(questionId, payload) => {
                  const { type: _type, ...rest } = payload;
                  return act(() => Api.LibraryUpdateQuestion(id, questionId, rest));
                }}
                onDelete={(questionId) => act(() => Api.LibraryDeleteQuestion(id, questionId))}
              />
            </section>
          ))}
        </div>

        <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          <section className="space-y-3 rounded-xl border border-border bg-card p-4">
            <h2 className="text-sm font-semibold text-foreground">{t('exam.library.details')}</h2>
            <label className="block space-y-1 text-sm">
              <span className="text-muted-foreground">{t('exam.library.name')}</span>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => title.trim() && title !== block.title && update({ title })} />
            </label>
            <label className="block space-y-1 text-sm">
              <span className="text-muted-foreground">{t('exam.source.tags')}</span>
              <Input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                onBlur={() => update({ tags: tags.split(',').map((s) => s.trim()).filter(Boolean) })}
                placeholder="education, science"
              />
            </label>
            <label className="block space-y-1 text-sm">
              <span className="text-muted-foreground">{t('exam.library.topic')}</span>
              <Input value={topic} onChange={(e) => setTopic(e.target.value)} onBlur={() => topic !== (block.topic ?? '') && update({ topic })} />
              <span className="block text-xs text-muted-foreground">{t('exam.library.topic_help')}</span>
            </label>
            <fieldset className="space-y-1 text-sm">
              <legend className="text-muted-foreground">{t('exam.difficulty')}</legend>
              <div className="flex gap-1.5">
                {(['easy', 'medium', 'hard'] as QuestionDifficulty[]).map((d) => (
                  <button
                    key={d}
                    type="button"
                    aria-pressed={block.difficulty === d}
                    onClick={() => update({ difficulty: d })}
                    className={cn('rounded-full border px-3 py-1 text-xs font-medium', block.difficulty === d ? 'border-foreground bg-foreground text-background' : 'border-border')}
                  >
                    {t(`exam.diff.${d}`)}
                  </button>
                ))}
              </div>
            </fieldset>
            {sat && (
              <label className="block space-y-1 text-sm">
                <span className="text-muted-foreground">{t('exam.library.threshold')}</span>
                <Input
                  type="number"
                  min={0}
                  max={200}
                  value={threshold}
                  disabled={!draft}
                  onChange={(e) => setThreshold(e.target.value)}
                  onBlur={() => threshold !== '' && Number(threshold) !== block.routingThreshold && update({ routingThreshold: Number(threshold) })}
                  className="w-28"
                />
                <span className="block text-xs text-muted-foreground">{t('exam.library.threshold_help')}</span>
              </label>
            )}
          </section>

          <section className="space-y-2 rounded-xl border border-border bg-card p-4">
            <h2 className="text-sm font-semibold text-foreground">{t('exam.library.versions')}</h2>
            <ul className="space-y-1.5 text-sm">
              {block.versions.map((v) => (
                <li key={v.id} className="flex items-center justify-between gap-2">
                  {v.id === block.id ? (
                    <span className="font-semibold">v{v.version}</span>
                  ) : (
                    <Link to={`/app/section-library/${v.id}`} className="underline-offset-2 hover:underline">
                      v{v.version}
                    </Link>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {t(`exam.library.status.${v.status.toLowerCase()}`)}
                    {v.isLatest && v.status === 'READY' && ` · ${t('exam.library.in_use')}`}
                    {` · ${t('exam.library.used', { count: v.usageCount })}`}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default SectionBlockPage;
